import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const HOST = process.env.REACT_APP_BACKEND_URL;
const STORY_DURATION = 5000; // 5 seconds per story

/**
 * Instagram-style story carousel.
 * Props:
 *   stories: array of gallery items (uses .id, .url, .caption, .category)
 *
 * Renders a row of circular thumbnails with red gradient rings.
 * Click opens a fullscreen player with timed progress bars at the top,
 * tap-to-advance / tap-to-go-back, and hold-to-pause.
 */
export default function StoryCarousel({ stories }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const tickRef = useRef(null);
  const startRef = useRef(0);
  const accumRef = useRef(0);

  const open = (idx) => { setOpenIdx(idx); setProgress(0); accumRef.current = 0; };
  const close = () => { setOpenIdx(null); setProgress(0); accumRef.current = 0; };

  const next = () => {
    if (openIdx === null) return;
    if (openIdx + 1 >= stories.length) return close();
    setOpenIdx(openIdx + 1);
    setProgress(0);
    accumRef.current = 0;
  };
  const prev = () => {
    if (openIdx === null || openIdx === 0) return;
    setOpenIdx(openIdx - 1);
    setProgress(0);
    accumRef.current = 0;
  };

  // Progress timer with pause support
  useEffect(() => {
    if (openIdx === null || paused) {
      if (tickRef.current) cancelAnimationFrame(tickRef.current);
      return;
    }
    startRef.current = performance.now();
    const tick = (now) => {
      const elapsed = accumRef.current + (now - startRef.current);
      const ratio = Math.min(elapsed / STORY_DURATION, 1);
      setProgress(ratio);
      if (ratio >= 1) {
        accumRef.current = 0;
        next();
      } else {
        tickRef.current = requestAnimationFrame(tick);
      }
    };
    tickRef.current = requestAnimationFrame(tick);
    return () => {
      if (tickRef.current) cancelAnimationFrame(tickRef.current);
      // Save progress on pause/unmount
      if (openIdx !== null) accumRef.current += performance.now() - startRef.current;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIdx, paused]);

  // Keyboard navigation when open
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIdx]);

  if (!stories || stories.length === 0) return null;

  return (
    <>
      {/* Story bubbles row */}
      <div className="flex gap-3 sm:gap-4 mb-6 overflow-x-auto scrollbar-none pb-2" data-testid="story-carousel">
        {stories.map((s, i) => (
          <button
            key={s.id}
            onClick={() => open(i)}
            data-testid={`story-bubble-${i}`}
            className="group flex flex-col items-center shrink-0 focus:outline-none"
          >
            <div className="relative">
              <div className="rounded-full p-[2px] bg-gradient-to-tr from-gg-red via-orange-500 to-yellow-400 group-hover:scale-105 transition-transform duration-300">
                <div className="rounded-full p-[2px] bg-gg-black">
                  <img
                    src={`${HOST}${s.url}`}
                    alt={s.caption || "Story"}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
                  />
                </div>
              </div>
            </div>
            <span className="font-heading text-[10px] uppercase tracking-widest text-zinc-400 group-hover:text-white mt-1.5 max-w-[80px] truncate">{s.caption || s.category || "Story"}</span>
          </button>
        ))}
      </div>

      {/* Fullscreen player */}
      <AnimatePresence>
        {openIdx !== null && stories[openIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black flex items-center justify-center select-none"
            data-testid="story-player"
          >
            {/* Progress bars */}
            <div className="absolute top-3 left-3 right-3 flex gap-1 z-20">
              {stories.map((_, i) => (
                <div key={i} className="flex-1 h-[2px] bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white transition-[width] ease-linear"
                    style={{
                      width: i < openIdx ? "100%" : i === openIdx ? `${progress * 100}%` : "0%",
                      transitionDuration: i === openIdx ? "0ms" : "200ms",
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Close button */}
            <button
              onClick={close}
              data-testid="story-close"
              className="absolute top-7 right-3 sm:top-8 sm:right-6 text-white/90 hover:text-white p-2 rounded-full hover:bg-white/10 transition z-20"
            >
              <X size={22} />
            </button>

            {/* Caption header */}
            <div className="absolute top-7 left-3 sm:top-8 sm:left-6 z-20 flex items-center gap-2">
              <div className="rounded-full p-[1.5px] bg-gradient-to-tr from-gg-red via-orange-500 to-yellow-400">
                <img src={`${HOST}${stories[openIdx].url}`} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border-[1.5px] border-black" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-[11px] uppercase tracking-wider text-white">{stories[openIdx].caption || "GG Fitness"}</span>
                {stories[openIdx].category && <span className="text-[9px] text-gg-red uppercase tracking-widest font-heading">{stories[openIdx].category}</span>}
              </div>
            </div>

            {/* Image */}
            <motion.img
              key={stories[openIdx].id}
              src={`${HOST}${stories[openIdx].url}`}
              alt={stories[openIdx].caption || "Story"}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-full max-h-full w-auto h-auto object-contain"
            />

            {/* Tap zones — left = prev, right = next, center = pause */}
            <button
              onMouseDown={() => setPaused(true)}
              onMouseUp={() => setPaused(false)}
              onTouchStart={() => setPaused(true)}
              onTouchEnd={() => setPaused(false)}
              onClick={prev}
              data-testid="story-prev-zone"
              className="absolute inset-y-0 left-0 w-1/3 z-10 cursor-pointer focus:outline-none"
              aria-label="Previous story"
            />
            <button
              onMouseDown={() => setPaused(true)}
              onMouseUp={() => setPaused(false)}
              onTouchStart={() => setPaused(true)}
              onTouchEnd={() => setPaused(false)}
              onClick={next}
              data-testid="story-next-zone"
              className="absolute inset-y-0 right-0 w-2/3 z-10 cursor-pointer focus:outline-none"
              aria-label="Next story"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
